import { Detector } from "./detectors";
import { ROI, SimpleBEROI } from "./imageAnnotations";
import { LabelValue } from "./labelValue";
import { NamedItem } from "./shared";

export enum ANSWER_VALUES {
  NO = "NO",
  YES = "YES",
  UNCLEAR = "UNCLEAR",
  __UNSURE = "__UNSURE",
  UNSURE = "UNSURE",
  NEEDS_REVIEW = "NEEDS_REVIEW",
  COUNT = "COUNT",
  GREATER_THAN_MAX = "GREATER_THAN_MAX",
}

export enum SOURCE_VALUES {
  HUMAN_CLOUD = "CLOUD",
  HUMAN_CLOUD_ENSEMBLE = "HUMAN_CLOUD_ENSEMBLE",
  HUMAN_CUSTOMER = "CUST",
  ALGORITHM = "ALG",
  ALGORITHM_RECONCILED = "ALG_REC",
  ALGORITHM_UNCLEAR = "ALG_UNCLEAR",
}

// Review reasons
// ESCALATION is when the IQ comes to the review queue because the ML model is not confident enough
// AUDIT is when the IQ is escalated to audit the quality of the ML model
// CUSTOMER INITIATED is when the customer decides to label the images query themselves for example
// in the query details modal
// ASSESMETN is when we send an IQ to test the performance of the labeler (just like a test in school).
export enum REVIEW_REASON_CHOICES {
  ESCALATION = "ESCALATION",
  AUDIT = "AUDIT",
  ASSESSMENT = "ASSESSMENT",
  CUSTOMER_INITIATED = "CUSTOMER_INITIATED",
  DISAGREEMENT = "DISAGREEMENT",
  DISAGREEMENT_ON_ASSESSMENT = "DISAGREEMENT_ON_ASSESSMENT",
}
export enum DATA_TYPE {
  IMAGE_QUERY = "ImageQuery",
  DISAGREEMENT = "Disagreement",
}

export interface DetectorSummary {
  num_image_queries: number;
  predictor__id: string;
  predictor__label_prompt: string;
  predictor__name: string;
}

export interface GroupSummary {
  num_image_queries: number;
  predictor__set__id: string;
  predictor__set__name: string;
  predictor__set__owner__name: string;
}

export interface ReviewPageSummaryData {
  detectors?: DetectorSummary[];
  groups?: GroupSummary[];
  global: number;
  unsure: number;
}

/**
 * Enum for the different types of bounding boxes that can be displayed for a given posicheck in the review pool.
 */
export enum ReviewBboxDisplayChoices {
  /**
   * No bounding boxes displayed.
   */
  NO_BOXES = "NO_BOXES",

  /**
   * Bounding boxes from recent ALG label value displayed.
   */
  ML_BOXES = "ML_BOXES",

  /**
   * Partial ML-generated bounding boxes displayed.
   */
  PARTIAL_ML_BOXES = "PARTIAL_ML_BOXES",

  /**
   * Unknown what bounding boxes that were displayed in the past before we introduced bounding box display choices.
   */
  UNKNOWN = "UNKNOWN",


  /**
   * Default value for review_bbox_display.
   */
  UNKNOWN_DEFAULT = "UNKNOWN_DEFAULT",
}

export type DisplayBBoxInfo = {
  display_bbox: ReviewBboxDisplayChoices;
  display_bbox_rois: ROI[] | null;
};

export interface ImageQueryBase {
  iq_id: string;
  image_id: string;
  created_at: string;
  is_placeholder: boolean;
  in_queue: boolean;
  human_labeled: boolean;
  current_best_answer_confidence: number | null;
  current_best_answer_source: SOURCE_VALUES | null;
  current_best_answer_str: string | null;
}
export interface ImageQuery extends ImageQueryBase {
  best_prediction_label_id: number | null;
  current_best_prediction_confidence: number | null;
  current_best_prediction_source: SOURCE_VALUES | null;
  current_best_prediction_str: string | null;
  current_best_prediction_rois: ROI[] | null;
  latest_flagged_label_feedback_text: string | null;
  flag_archived: boolean;
  confidence_threshold: number;
  ground_truth_confidence: number | null;
  ground_truth_source: SOURCE_VALUES | null;
  ground_truth_str: ANSWER_VALUES | null;
  ml_answer_confidence: number | null;
  ml_answer_str: ANSWER_VALUES | null;
  ml_prediction_confidence: number | null;
  ml_prediction_str: ANSWER_VALUES | null;
  patience_time: number;
  predictor_id: string;
  predictor_name: string;
  query: string;
  system_answer_confidence: number | null;
  system_answer_source: SOURCE_VALUES | null;
  system_answer_str: ANSWER_VALUES | null;
  system_prediction_confidence: number | null;
  system_prediction_source: SOURCE_VALUES | null;
  system_prediction_str: ANSWER_VALUES | null;
  is_audited: boolean;
  last_label?: LabelValue;
}

export interface ResultBlob {
  class_name?: string;
  source?: string;
  confidence?: number;
  rois?: ROI[];
  _history?: ResultHistory[];
  label_value_serialized?: {
    feedback_text?: string;
  };
}
export interface Posicheck {
  id: string;
  data_type: DATA_TYPE;
  owner: NamedItem;
  predictor: Detector;
  review_requested_priority: number;
  image_id: string;
  patience_time: number;
  created_at: string;
  confidence_threshold: number;
  review_bbox_info: DisplayBBoxInfo;
  best_prediction?: {
    id: number;
    created_by_id?: number;
    created_at: string;
    image_id: string;
    detector_id: number;
    value?: number;
    class_name: string;
    confidence?: number;
    posicheck_id: string;
    feedback_text: null;
    rois: SimpleBEROI[] | null;
    source: string;
    elapsed_screen_time_ms: null;
    flag_archived: null;
    deleted_at: null;
    annotations_requested: Array<string>;
  };
  human_review_label: {
    id: string;
    created_by_id: string;
    created_at: string;
    image_id: string;
    detector_id: string;
    value: string;
    class_name: string;
    posicheck_id: string;
    feedback_text?: string;
    elapsed_screen_time_ms?: string;
  } | null;
  review_reason: REVIEW_REASON_CHOICES;
  in_review_queue?: string;
}
export interface ResultHistory {
  type?: string;
  source?: string;
  version?: string;
  class_name: string;
  confidence?: number;
  logic_type?: string;
  model_arch?: string;
  logic_config?: {
    model_arch?: string;
    distance_threshold?: number;
    zero_shot_text_mode?: boolean;
  };
}
